import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined"
import SubjectIcon from "@mui/icons-material/Subject"
import { RoundedTab, Tabs } from "@synapse-analytics/synapse-ui"

import type { GraphJsonSwitchProps } from "../../../interfaces"

import styles from "./GraphJsonToggle.module.scss"

/**
 * A component that provides a toggle switch between JSON and Graph view modes.
 * It displays two tabs: one for JSON mode and one for Graph mode, allowing users to switch between them.
 *
 * @param {boolean} isJsonMode - Indicates if the current mode is JSON.
 * @param {function} handleSwitchMode - Function to handle switching between JSON and Graph modes.
 * @returns {ReactElement} A Tabs component with two RoundedTab components for switching modes.
 */
export function GraphJsonSwitch(props: Readonly<GraphJsonSwitchProps>): ReactElement {
  const { isJsonMode, handleSwitchMode } = props
  const { t } = useTranslation("decisions", { keyPrefix: "graphJsonSwitch" })

  const jsonLabel = t("json")
  const graphLabel = t("graph")

  return (
    <Tabs activeTab={isJsonMode ? jsonLabel : graphLabel} className={styles.tabsContainer}>
      <RoundedTab
        value={jsonLabel}
        icon={() => <SubjectIcon style={{ width: "16px", height: "16px" }} />}
        size="default"
        selected={isJsonMode}
        onClick={handleSwitchMode}
      />
      <RoundedTab
        value={graphLabel}
        icon={() => <RouteOutlinedIcon style={{ width: "16px", height: "16px" }} />}
        size="default"
        selected={!isJsonMode}
        onClick={handleSwitchMode}
      />
    </Tabs>
  )
}
